<template>
  <form class="score-gate" novalidate @submit.prevent="emailValid && !submitting && emit('submit')">
    <span class="m600 sec-label">{{ gate.eyebrow }}</span>
    <p class="o500 score-gate__title" tabindex="-1" data-scorer-focus>{{ gate.title }}</p>

    <div class="score-gate__fields">
      <label class="score-gate__field">
        <span class="m600 sec-label">{{ gate.emailLabel }}</span>
        <input
          class="m500"
          type="email"
          required
          :placeholder="gate.emailPlaceholder"
          autocomplete="email"
          :value="email"
          @input="emit('update:email', $event.target.value)"
        />
      </label>
      <label class="score-gate__field">
        <span class="m600 sec-label">{{ gate.bandLabel }}</span>
        <select class="m500" :value="band" @change="emit('update:band', $event.target.value)">
          <option v-for="b in gate.bands" :key="b" :value="b">{{ b }}</option>
        </select>
      </label>
    </div>

    <p class="m500 score-gate__hint">{{ gate.hint }}</p>

    <div>
      <sec-button type="submit" :disabled="!emailValid || submitting">{{ gate.submit }}</sec-button>
    </div>
  </form>
</template>

<script setup>
// Results gate. "Show my results" stays disabled until a valid email is entered; the
// parent (SecScorer) then subscribes that email to the newsletter and shows the result.
import { computed } from 'vue';
import SecButton from '@/components/security/ui/SecButton.vue';
import { SCORER_COPY } from '@/content/security/score';

const props = defineProps({
  email: { type: String, default: '' },
  band: { type: String, default: '' },
  submitting: { type: Boolean, default: false },
});

const emit = defineEmits(['update:email', 'update:band', 'submit']);

const gate = SCORER_COPY.gate;

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const emailValid = computed(() => EMAIL.test(props.email.trim()));
</script>

<style scoped>
.score-gate {
  display: flex;
  flex-direction: column;
  gap: var(--sec-s3);
}

.score-gate__title {
  font-size: 1.15rem;
  line-height: 135%;
  color: var(--sec-ink);
}

.score-gate__fields {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: var(--sec-s3);
  max-width: 640px;
}

.score-gate__field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.score-gate__field input,
.score-gate__field select {
  height: 48px;
  padding: 0 12px;
  font-size: 0.95rem;
  color: var(--sec-ink);
  background: var(--sec-surface);
  border: 1px solid var(--sec-line-strong);
  border-radius: var(--sec-radius);
  appearance: auto;
}

.score-gate__field input:focus,
.score-gate__field select:focus {
  outline: none;
  border-color: var(--sec-accent);
  box-shadow: 0 0 0 3px var(--sec-accent-ring);
}

.score-gate__field input::placeholder {
  color: var(--sec-ink-3);
}

.score-gate__hint {
  font-size: 0.88rem;
  color: var(--sec-ink-3);
  max-width: var(--sec-measure);
}

@media only screen and (min-width: 700px) {
  .score-gate__title {
    font-size: 1.45rem;
  }
}
</style>
